import { organizationsApi } from "api";
import { useCallback, useContext, useEffect, useRef } from "react";
import {
  Organization,
} from "model/organizationModel";
import { OrganizationsContext } from "contexts";
import useUser from "./useUser";

let inFlight: { userId: number; request: Promise<{ data: { organizations: Organization[] } }> } | null = null;

const useOrganizations = (
  sleep?: number,
): [Organization[], () => Promise<void>] => {
  const {
    state: { organizationsUserId, organizations },
    actions: { setOrganizations, resetOrganizations },
  } = useContext(OrganizationsContext);
  const user = useUser();
  const userId = user?.userId;
  const activeUserRef = useRef(userId);
  activeUserRef.current = userId;
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const getOrganizations = useCallback(async (force = false) => {
    const activeUserId = user?.userId;
    if (!activeUserId) {
      resetOrganizations();
      return;
    }

    const pending = !force && inFlight?.userId === activeUserId
      ? inFlight
      : { userId: activeUserId, request: organizationsApi.get<{ organizations: Organization[] }>('user') };
    inFlight = pending;
    try {
      const { data } = await pending.request;
      if (!isMountedRef.current || activeUserRef.current !== activeUserId) {
        return;
      }
      setOrganizations(data.organizations);
    } catch {
      if (isMountedRef.current && activeUserRef.current === activeUserId) {
        setOrganizations([]);
      }
    } finally {
      if (inFlight === pending) {
        inFlight = null;
      }
    }
  }, [setOrganizations, resetOrganizations, user]);

  useEffect(() => {
    if (!userId) {
      if (organizations.length > 0 || organizationsUserId !== null) {
        resetOrganizations();
      }
      return;
    }

    if (organizationsUserId === userId) {
      return;
    }

    const start = () => {
      getOrganizations();
    };

    if (sleep) {
      const timeoutId = setTimeout(start, sleep);
      return () => clearTimeout(timeoutId);
    }

    start();
  }, [
    userId,
    organizationsUserId,
    sleep,
    getOrganizations,
    setOrganizations,
    resetOrganizations,
    organizations.length,
  ]);

  return [organizations, () => getOrganizations(true)];
};

export default useOrganizations;
